import React from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";

import { ResponsiveSelect } from "../../../../../components/MobileForm";
import { ActivityOrder } from "../../../../../services/holdings";
import { ActivityDateFilter } from "../../../types";
import { ActivityHoldingFilter } from "./ActivityHoldingFilter";
import { ActivityTypeFilter } from "./ActivityTypeFilter";
import { ACTIVITY_DATE_OPTIONS, ACTIVITY_ORDER_OPTIONS } from "./constants";
import {
  SearchClearButton,
  SearchField,
  SearchIcon,
  SearchInput,
  Toolbar,
  ToolbarGroup,
} from "./styles";
import { ActivityDatePreset, ActivityToolbarProps } from "./types";

const presetOf = (date: ActivityDateFilter): ActivityDatePreset =>
  date.kind === "lastDays"
    ? (String(date.days) as ActivityDatePreset)
    : date.kind;

const ActivityToolbarComponent: React.FC<ActivityToolbarProps> = ({
  searchInput,
  onSearchChange,
  date,
  onDateChange,
  years,
  holdingOptions,
  holdingIds,
  accountLevel,
  onHoldingsChange,
  types,
  onTypesChange,
  order,
  onOrderChange,
  showOrderSelect,
  isCompact,
}) => {
  const handlePresetChange = (preset: ActivityDatePreset) => {
    if (preset === "currentYear" || preset === "allTime") {
      onDateChange({ kind: preset });
    } else if (preset === "year") {
      onDateChange({ kind: "year", year: years[0] });
    } else {
      onDateChange({ kind: "lastDays", days: Number(preset) as 7 | 30 });
    }
  };

  const yearOptions = years.map((year) => ({
    value: String(year),
    label: String(year),
  }));

  return (
    <Toolbar>
      <SearchField>
        <SearchIcon>
          <SearchRoundedIcon />
        </SearchIcon>
        <SearchInput
          type="search"
          value={searchInput}
          aria-label="Search activity"
          placeholder="Search activity"
          onChange={(event) => onSearchChange(event.target.value)}
        />
        {searchInput !== "" && (
          <SearchClearButton
            size="small"
            aria-label="Clear search"
            onClick={() => onSearchChange("")}
          >
            <CloseRoundedIcon />
          </SearchClearButton>
        )}
      </SearchField>

      <ToolbarGroup>
        <ResponsiveSelect
          isCompact={isCompact}
          options={ACTIVITY_DATE_OPTIONS}
          value={presetOf(date)}
          onChange={(option) =>
            handlePresetChange(option.value as ActivityDatePreset)
          }
          sheetTitle="Filter by date"
          testId="activity-date-filter"
        />

        {date.kind === "year" && (
          <ResponsiveSelect
            isCompact={isCompact}
            options={yearOptions}
            value={String(date.year)}
            onChange={(option) =>
              onDateChange({ kind: "year", year: Number(option.value) })
            }
            sheetTitle="Filter by year"
            testId="activity-year-filter"
          />
        )}

        <ActivityHoldingFilter
          holdingOptions={holdingOptions}
          holdingIds={holdingIds}
          accountLevel={accountLevel}
          onChange={onHoldingsChange}
          isCompact={isCompact}
        />

        <ActivityTypeFilter
          selected={types}
          onChange={onTypesChange}
          isCompact={isCompact}
        />

        {showOrderSelect && (
          <ResponsiveSelect
            isCompact={isCompact}
            options={ACTIVITY_ORDER_OPTIONS}
            value={order}
            onChange={(option) => onOrderChange(option.value as ActivityOrder)}
            sheetTitle="Sort activity"
            testId="activity-order"
          />
        )}
      </ToolbarGroup>
    </Toolbar>
  );
};

export const ActivityToolbar = React.memo(ActivityToolbarComponent);
